import { createReducer } from '@reduxjs/toolkit';
import {
    getAllProducts,
    getPrimaryOrSecondaryProducts,
    getProduct
} from './next-shipping-selectors';
import { ensureGoodShippingOptions } from './ensure-good-shipping-options';
import { validate } from './validate';
import { getSelectedShippingOptions } from './get-selected-shipping-options';

const initialState = validate({
    primaryShippingProviders: [],
    secondaryShippingProviders: [],
    validationError: null,
    secondaryShippingOptionOpen: false,
    selectedShippingOptions: [],
    suggestedInvalidProducts: {},
    hasSuggestedProductDescriptions: false,
    invalidShippingCosts: {},
    takeawayDeliveryAccepted: false,
    showMoreOptions: false,
    showMoreSecondaryOptions: true,
    weight: 0,
    isLoadingProvidersWithProducts: false
});

const getProviderByProductType = (providers, productType) =>
    providers.find(
        provider =>
            !!provider.products.find(product => product.type === productType)
    );

export default createReducer(initialState, {
    SET_SUGGESTED_SHIPPING_PRODUCTS: (state, action) => {
        // TODO Re-implement!

        state.hasSuggestedProductDescriptions = false;

        const products = getAllProducts(state);
        products.forEach(product => {
            const suggestedProduct = action.shippingProducts.find(
                p => p.type === product.type
            );
            if (suggestedProduct) {
                product.cost = suggestedProduct.cost || product.cost;
                state.hasSuggestedProductDescriptions = true;
                product.calculatorSuggestion = {
                    description: suggestedProduct.description,
                    cost: suggestedProduct.cost,
                    additionalInfo: suggestedProduct.additionalInfo
                };
            } else {
                product.calculatorSuggestion = null;
            }
            product.selected = false;
        });
        return validate(state);
    },
    RESET_SHIPPING_OPTIONS: state => {
        state.primaryShippingOption = null;
        state.secondaryShippingOption = null;
        state.selectedShippingOptions = [];
        return validate(state);
    },
    RESET_SELECTED_SHIPPING_OPTIONS: state => {
        const products = getAllProducts(state);
        products.forEach(product => {
            product.selected = false;
            product.selectedVariant = null;
            product.productId = null;
        });
        state.selectedShippingOptions = getSelectedShippingOptions(state);
        return validate(state);
    },
    SET_SHIPPING_OPTIONS: (state, action) => {
        const shippingOptions = ensureGoodShippingOptions(action.payload);

        // Set a product to visible and hides the rest, if the type to be visible is found in the list.
        const updateVisibilities = (products, typeToBeVisible) => {
            if (products.length === 1) return;
            const typeToBeVisibleExists = products.find(
                product => product.type === typeToBeVisible
            );
            if (!typeToBeVisibleExists) return;
            products.forEach(
                product => (product.visible = product.type === typeToBeVisible)
            );
        };

        const updateProductIfType = (
            product,
            { type, cost, productId, weight }
        ) => {
            const isType = product.type === type;
            if (isType) {
                product.selected = true;
                product.cost = cost;
                product.productId = productId;
                product.weight = weight;
            } else {
                product.selected = false;
            }
        };

        const updateProviderProducts = (products, option) => {
            products.forEach(product => updateProductIfType(product, option));
            updateVisibilities(products, option.type);
        };

        if (_.isObject(shippingOptions[0])) {
            const option = shippingOptions[0];
            const providers = state.primaryShippingProviders;
            providers.forEach(provider =>
                updateProviderProducts(provider.products, option)
            );
            const provider = getProviderByProductType(providers, option.type);
            state.showMoreOptions =
                state.showMoreOptions || (provider && !provider.promoted);
        }

        if (_.isObject(shippingOptions[1])) {
            const option = shippingOptions[1];
            const providers = state.secondaryShippingProviders;
            providers.forEach(provider =>
                updateProviderProducts(provider.products, option)
            );
            const provider = getProviderByProductType(providers, option.type);
            state.showMoreSecondaryOptions =
                state.showMoreSecondaryOptions ||
                (provider && !provider.promoted);
            state.secondaryShippingOptionOpen = true;
        } else {
            state.secondaryShippingOptionOpen = false;
        }

        state.selectedShippingOptions = getSelectedShippingOptions(state);

        return validate(state);
    },
    TOGGLE_SHIPPING_OPTION: (state, action) => {
        const providers = action.payload.primary
            ? state.primaryShippingProviders
            : state.secondaryShippingProviders;

        // Hide products so only one is visible per provider
        const productProvider = getProviderByProductType(
            providers,
            action.payload.type
        );
        productProvider.products.forEach(product => (product.visible = false));

        const products = providers.flatMap(provider => provider.products);
        let product = products.find(p => p.type === action.payload.type);
        let previousValue = product.selected;
        products.forEach(p => (p.selected = false));
        product.selected = !previousValue;
        product.visible = true;
        state.selectedShippingOptions = getSelectedShippingOptions(state);
        return validate(state);
    },
    SET_SHIPPING_OPTION_COST: (state, action) => {
        let product = getProduct(
            state,
            action.payload.primary,
            action.payload.type
        );
        product.cost = action.payload.cost;
        product.isBulkySelected = false;
        state.selectedShippingOptions = getSelectedShippingOptions(state);
        return validate(state);
    },
    SET_SHIPPING_OPTION_VARIANT: (state, action) => {
        let product = getProduct(
            state,
            action.payload.primary,
            action.payload.type
        );
        product.selectedVariant = action.payload.variant;
        product.weight = action.payload.variant
            ? action.payload.variant.weight
            : undefined;
        product.productId = action.payload.variant
            ? action.payload.variant.id
            : undefined;
        state.selectedShippingOptions = getSelectedShippingOptions(state);
        return validate(state);
    },
    SET_TAKEAWAY_SELECTED: (state, action) => {
        state.takeawayDeliveryAccepted = action.payload.takeawaySelected;
        state.selectedShippingOptions = getSelectedShippingOptions(state);
        return validate(state);
    },
    SET_SECONDARY_SHIPPING_OPTION_OPEN: (state, action) => {
        return validate(
            _.assign({}, state, {
                secondaryShippingOptionOpen:
                    action.payload.secondaryShippingOptionOpen
            })
        );
    },
    REMOVE_SHIPPING_OPTION: (state, action) => {
        getPrimaryOrSecondaryProducts(state, action.payload.primary).forEach(
            product => (product.selected = false)
        );
        state.selectedShippingOptions = getSelectedShippingOptions(state);
        return validate(state);
    },
    MAKE_SECONDARY_SHIPPING_OPTION_PRIMARY: state => {
        const tmp = state.primaryShippingProviders;
        state.primaryShippingProviders = state.secondaryShippingProviders;
        state.secondaryShippingProviders = tmp;
        state.selectedShippingOptions = getSelectedShippingOptions(state);
        return validate(state);
    },
    FORCE_FREE_SHIPPING: state => {
        const products = getAllProducts(state);
        products.forEach(product => {
            product.cost = 0;
        });
        state.selectedShippingOptions = getSelectedShippingOptions(state);
        return validate(state);
    },
    SHOW_MORE_SHIPPING_OPTIONS: (state, action) => {
        if (action.payload.isPrimary) {
            state.showMoreOptions = true;
        } else {
            state.showMoreSecondaryOptions = true;
        }
        return validate(state);
    },
    TOGGLE_MORE_SHIPPING_OPTIONS: (state, action) => {
        const { isPrimary } = action.payload;
        if (isPrimary) {
            state.showMoreOptions = !state.showMoreOptions;
        } else {
            state.showMoreSecondaryOptions = !state.showMoreSecondaryOptions;
        }
        return validate(state);
    },
    TOGGLE_PRODUCT_EXTRA_INFO_VISIBILITY: (state, action) => {
        const product = getProduct(
            state,
            action.payload.primary,
            action.payload.productType
        );
        product.isExtraInfoVisible = !product.isExtraInfoVisible;
        return validate(state);
    },
    SET_SHIPPING_PRODUCT_BULKY_SELECTED: (state, action) => {
        const product = getProduct(
            state,
            action.payload.primary,
            action.payload.productType
        );
        product.isBulkySelected = action.payload.isBulkySelected;
        return validate(state);
    },
    SET_SHIPPING_PROVIDERS_WITH_PRODUCTS: (state, action) => {
        state.primaryShippingProviders = action.payload;
        state.secondaryShippingProviders = action.payload;
        state.isLoadingProvidersWithProducts = false;

        return validate(state);
    },
    SET_SHIPPING_PROVIDERS_WITH_PRODUCTS_FETCHING: state => {
        state.isLoadingProvidersWithProducts = true;

        return validate(state);
    }
});
